import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import { MutableRefObject } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useGetMenuMaster } from '@api/menu';
import { IconLogout } from '@tabler/icons-react';
import AnimateButton from '@components/extended/AnimateButton';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import { getUserInitials } from '@utils/utils';

type SidebarUserAvatarProps = {
  open: boolean;
  handleToggle: () => void;
  handleLogout: () => void;
  anchorRef: MutableRefObject<any>;
};

function SidebarUserAvatar({
  open,
  handleToggle,
  anchorRef,
  handleLogout,
}: SidebarUserAvatarProps) {
  const theme = useTheme();
  const userData = useAuthSelector((state) => state.userData);
  const renewUserImageLink = useAuthSelector(
    (state) => state.renewUserImageLink
  );
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const userInitials = userData?.name ? getUserInitials(userData.name) : '';
  const hasPhoto = Boolean(userData?.image_url);

  return (
    <Box sx={{ display: 'flex', paddingX: '16px', flexDirection: 'column' }}>
      <Divider />
      <Stack
        paddingY="16px"
        direction="row"
        spacing={0.5}
        justifyContent={drawerOpen ? 'start' : 'center'}
      >
        <Avatar
          src={hasPhoto ? userData?.image_url : undefined}
          alt="user-images"
          imgProps={{ onError: renewUserImageLink }}
          onClick={handleToggle}
          sx={{
            ...theme.typography.mediumAvatar,
            cursor: 'pointer',
            backgroundColor: hasPhoto
              ? theme.palette.background.default
              : theme.palette.primary.main,
            color: hasPhoto ? undefined : 'white',
            fontSize: hasPhoto ? undefined : '14px',
          }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          color="inherit"
        >
          {!hasPhoto && userInitials}
        </Avatar>
        {drawerOpen && (
          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
            <Stack sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  component="span"
                  variant="h5"
                  sx={{ fontWeight: 400 }}
                >
                  {userData?.name}
                </Typography>
              </Stack>
              <Typography
                variant="subtitle2"
                sx={{
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                {userData?.email}
              </Typography>
            </Stack>
            <AnimateButton>
              <Tooltip title="Logout">
                <IconButton color="primary" size="small" onClick={handleLogout}>
                  <IconLogout stroke={1.5} size="20px" />
                </IconButton>
              </Tooltip>
            </AnimateButton>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default SidebarUserAvatar;
